// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Previous from "@awc/XDL/Icons/IconPaginationSmallLeft";
import Next from "@awc/XDL/Icons/IconPaginationSmallRight";
import { fontConfig } from "@awc/XDL/PreStyle/typography";
import { xBaseTheme } from "@awc/XDL/types";
import { color, xScreen } from "@awc/XDL/Utilities";
import { IconWrapper } from "@insite/mobius/Icon";
import PaginationPresentationPropsDefault from "@insite/mobius/Pagination/presentationProps";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { css } from "styled-components";

export default {
    defaultProps: {
        ...PaginationPresentationPropsDefault,
        mergeCss: true,
        selectProps: {
            cssOverrides: {
                formField: css`
                    display: flex;
                    align-items: center;
                    ${xScreen("lte", "sm")} {
                        flex-direction: row;
                        gap: 8px;
                    }
                `,
                formInputWrapper: css`
                    width: auto;
                    max-width: 75px;
                    min-width: 64px;
                    ${xScreen("lte", "sm")} {
                        &&&&& {
                            margin-top: 0;
                        }
                    }
                `,
                inputSelect: css`
                    font-size: 14px;
                `,
            },
            iconProps: {
                css: css`
                    &&&& {
                        padding: 8px;
                        width: auto;
                        height: 28px;
                    }
                `,
            },
            labelProps: {
                mergeCss: true,
                transform: "capitalize",
                weight: fontConfig.weight.bold,
                color: "text",
                css: css`
                    min-height: initial;
                    letter-spacing: 0;
                    line-height: ${fontConfig.lineHeight.singleLineSmall};
                    display: flex;
                    align-items: center;
                    font-size: ${fontConfig.fontSize.small}px;
                    width: fit-content;
                    ${xScreen("lte", "sm")} {
                        white-space: nowrap;
                    }
                `,
            },
            css: css`
                &&&&&& {
                    padding-right: 28px;
                    max-height: 32px;
                }
            `,
        },
        navIconsSrc: {
            firstPage: "ChevronsLeft",
            previousPage: Previous,
            nextPage: Next,
            lastPage: "ChevronsRight",
        },

        buttonProps: {
            mergeCss: true,
            sizeVariant: "small",
            typographyProps: {
                size: fontConfig.fontSize.xSmall,
                css: css`
                    letter-spacing: 0;
                    line-height: ${fontConfig.lineHeight.xSmall};
                `,
            },
            css: css`
                min-height: 18px;
                border: none;
                ${IconWrapper} {
                    color: ${color("primary.main")};
                }
                &:disabled {
                    background: transparent;
                    ${IconWrapper} {
                        color: ${color("common.ui4", 0.33)};
                    }
                }
                &:hover:not(:disabled) {
                    background: ${color("common.ui1")};
                    border: none;
                    color: ${color("common.ui1")};
                    ${IconWrapper} {
                        color: ${color("common.ui8")};
                    }
                }
                &:focus {
                    background: transparent;
                }
            `,
        },
        cssOverrides: {
            ...PaginationPresentationPropsDefault?.cssOverrides,
            ellipsis: css`
                margin: 0 10px;
            `,
            linkList: css`
                & > ul {
                    display: flex;
                    align-items: center;
                }
                & > .pagination-container {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    & > .pagination-element {
                        display: flex;
                        align-items: center;
                        gap: 12px;
                        & .page-count {
                            white-space: nowrap;
                            text-transform: lowercase;
                        }
                        & .current-page-field {
                            width: 48px;
                            text-align: center;
                        }
                    }
                }
            `,
            pagination: css`
                gap: 60px;
                ${xScreen("lte", "xs")} {
                    gap: 8px;
                }
                &.add-border {
                    padding-inline: 12px;
                    border: 1px solid ${color("primary.main")};
                    box-shadow: 0 3px 3px 0 ${color("common.ui7", 0.3)};
                }
                ${({ theme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                margin: 12px 0;
                                flex-wrap: nowrap;
                            `,
                            css`
                                flex-wrap: nowrap;
                            `,
                            null,
                            css`
                                margin: 12px 0;
                            `,
                            null,
                        ],
                        "min",
                    )};
                margin: 12px 0;
                ${({ justifyContent }: any) => justifyContent && `justify-content: ${justifyContent};`}
                ${xScreen("lte", "sm")} {
                    justify-content: space-between;
                    width: 100%;
                }
            `,
            arrowButtons: css`
                &:focus-within {
                    outline: 1px solid ${color("primary")};
                }
            `,
        },
    },
} as xBaseTheme["pagination"];
